import { useProductLink } from 'src/sdk/product/useProductLinkV2'
import { Image } from 'src/components/ui/Image'
import type { Product } from 'src/components/restructure/product/types/product'
import { formatterPrice } from 'src/utils/formatterPrice'
import { getProductBenefits } from 'src/components/restructure/product/sections/GalleryProductBenefits/utils'

import { WrapperElement } from '../shared/WrapperLink'

interface KitlookProductCardProps {
  product: Product
}

export function KitlookProductCard({ product }: KitlookProductCardProps) {
  const firstAvailable = product.items.find(
    (item) => item.isAvailable && item.productId === product.productId
  )

  const img = firstAvailable?.images[0]

  const productBenefitsData = firstAvailable?.productBenefits ?? null
  const productBenefitsArray = getProductBenefits(productBenefitsData)
  const firstProductBenefit = productBenefitsArray.find(
    (benefit) => benefit.image
  )

  const productLink = useProductLink({
    product,
    currentSku: firstAvailable,
    index: 0,
    slug: product.linkText,
    shelfRef: `Vitrine: Kitlook${
      product?.productId ? ` - ${product?.productId}` : ''
    }`,
  })

  if (!firstAvailable) {
    return null
  }

  const {
    sellerOffer: { price, listPrice },
  } = firstAvailable.sellerDefault

  return (
    <WrapperElement
      productLink={productLink}
      enableCardLink
      className="w-full h-full max-w-[137px] md:max-w-[217px] flex-col gap-2"
    >
      <div className="bg-restructure-background-secondary md:bg-transparent rounded-t-md md:rounded-t-none">
        <div className="mix-blend-multiply bg-restructure-background-secondary border-b-2 border-gray md:border-none">
          {img && (
            <Image
              src={img.imageUrl}
              alt={img?.imageText ?? product.productName}
              className="w-40 md:w-[364px] md:min-w-[160px] rounded-t-md md:rounded-t-none max-w-full mix-blend-multiply bg-restructure-background-secondary"
              width={364}
            />
          )}
        </div>
      </div>

      <div className="w-full bg-white md:bg-transparent px-3 pb-3 pt-[7.67px] md:p-0 rounded-b-md md:rounded-b-none md:mt-2">
        {firstProductBenefit && (
          <div className="md:border-2 md:border-neutral04 md:p-2 flex items-center gap-1 mb-3">
            <img
              src={firstProductBenefit.image ?? ''}
              alt={firstProductBenefit.title}
              width={20}
              className="grayscale"
              onLoad={(event) => {
                const image = event.currentTarget

                if (image.naturalWidth <= 2 && image.naturalHeight <= 2) {
                  image.src =
                    'https://decathlonstoreapp.vteximg.com.br/arquivos/galleryProductBenefitDefault.svg'
                }
              }}
              onError={(event) => {
                const image = event.currentTarget

                image.src =
                  'https://decathlonstoreapp.vteximg.com.br/arquivos/galleryProductBenefitDefault.svg'
              }}
            />
            <span className="font-medium text-[10px] text-neutral10">
              {firstProductBenefit.title}
            </span>
          </div>
        )}
        <p className="text-xs text-neutral10 font-medium line-clamp-1 md:line-clamp-2 mb-3">
          {product.productName}
        </p>
        <div className="flex items-center flex-wrap gap-2">
          <span className="text-neutral10 text-sm font-semibold">
            {formatterPrice(price)}
          </span>
          {listPrice > price && (
            <span className="text-[8px] md:text-[10px] text-redDiscount font-semibold line-through">
              {formatterPrice(listPrice)}
            </span>
          )}
        </div>
      </div>
    </WrapperElement>
  )
}
